(ns ui.view.card.attachments.view
  (:require
   [re-frame.core :as rf]
   [common.localization :as loc]
   [ui.view.card.attachments.sub :as sub]
   [ui.view.card.attachments.event :as event]
   ["react-dropzone" :refer [useDropzone]]
   [ui.view.card.shared-components.view :as card-shared]
   [ui.view.card.shared-components.full-screen-card :as full-screen-card]
   [ui.view.card.shared-components.card-container :as card-container]
   [ui.view.card.shared-components.card-title :as card-title]
   [ui.components.radix-ui.progress.view :refer [progress-primitive
                                                 progress-indicator-primitive]]
   [oops.core :as oops]
   [ui.util :as uu]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.folder-selection-modal.view :as folder-selection-modal]
   [clojure.string :as string]
   [ui.enrich.existing :as enrich-existing]))

(defn file-icon-class
  [content-type]
  (let [image? (some-> content-type
                       (string/starts-with? "image"))
        _csv? (= content-type "text/csv")
        pdf? (= content-type "application/pdf")]
    (cond image? "fal fa-image"
          pdf? "fal fa-file-pdf"
          :else "fal fa-file")))

(defn progress-bar
  [{:keys [value]
    :or {value 0}}]
  (let [value (min value 100)]
    [progress-primitive
     {:class "relative h-2 w-full overflow-hidden rounded-full bg-base-regular"
      :value value}
     [progress-indicator-primitive
      {:class "h-full w-full flex-1 bg-primary-regular  transition-all"
       :style {:transform (str "translateX(-" (- 100 value) "%)")}}]]))

(defn increment-fake-progress
  [progress]
  (cond
    (< 95 progress)
    (+ progress 0.1)
    (< 90 progress)
    (+ progress 0.2)
    (< 80 progress)
    (+ progress 1)
    (< 70 progress)
    (+ progress 3)
    (< 60 progress)
    (+ progress 5)
    (< 40 progress)
    (+ progress 7)
    :else (+ progress 10)))

(defn fake-progress-bar
  [{:keys [value]}]
  (let [interval-atom (atom nil)
        progress-atom (r/atom value)]
    (r/create-class
     {:component-did-mount (fn []
                             (reset! interval-atom (js/setInterval
                                                    #(swap! progress-atom increment-fake-progress)
                                                    1000)))
      :component-will-unmount (fn []
                                (when-let [interval @interval-atom]
                                  (js/clearInterval interval)))
      :reagent-render
      (fn [{:keys [value]}]
        (let [value (if (= value 100)
                      value
                      @progress-atom)]
          [progress-bar {:value value}]))})))

(defn dropzone-inner
  [{:keys [ctx]}]
  (let [on-drop (fn [files]
                  (rf/dispatch [::event/upload-files ctx files]))
        hook-result (useDropzone (clj->js {:onDrop on-drop}))
        root-props (-> hook-result
                       (oops/ocall "getRootProps")
                       (js->clj :keywordize-keys true))
        input-props (-> hook-result
                        (oops/ocall "getInputProps")
                        (js->clj :keywordize-keys true))
        drag-active? (oops/oget hook-result "isDragActive")]
    [:div
     (merge
      root-props
      {:class (uu/cm "relative rounded flex items-center justify-center border border-dashed py-7 hover:bg-complement-light hover:shadow-inner"
                     (when drag-active?
                       "shadow-inner bg-complement-light border-primary-regular"))})
     [:input
      (merge
       input-props
       {:class "bg-red-500"})]
     [:div
      {:class "flex items-center gap-3 pointer-events-none"}
      [:i
       {:class "fal fa-cloud-upload text-2xl text-base-light-text-paler"}]
      [:span
       [:span
        {:class "text-sm m-0 text-base-light-text-paler"}
        (loc/tr :dropzone-instructions-pre-link nil :en)]
       [:span
        {:class "text-sm m-0 text-base-light-text-accent cursor-pointer"}
        (loc/tr :dropzone-instructions-link nil :en)]]]]))

(defn dropzone
  [props]
  ;; Make functional so dropzone inner can use the react hook
  [:f> dropzone-inner props])

(defn attachment-item-left
  [{:keys [filename content-type]}]
  [:div
   {:class "flex gap-2 items-center"}
   [:i {:class (file-icon-class content-type)}]
   [:div
    {:class "font-light py-2 text-start"}
    filename]])

(defn uploads-in-progress
  [{:keys [ctx]}]
  (let [uploads @(rf/subscribe [::sub/uploads-in-progress ctx])]
    [:div
     {:class "flex flex-col"}
     (->> uploads
          (map-indexed
           (fn [index {:keys [filename progress content-type]}]
             ^{:key (str "upload-" index)}
             [:div
              {:class "flex items-center justify-between gap-2"}
              [attachment-item-left
               {:filename filename
                :content-type content-type}]
              [:div
               {:class "w-56"}
               [fake-progress-bar {:value progress}]]])))]))

(defn attachment-actions
  [{:keys [attachment open-delete-confirmation-modal ctx]}]
  (let [id (:db/id attachment)
        folder-names @(rf/subscribe [::sub/folder-names ctx])
        primary-entity @(rf/subscribe [::sub/primary-entity ctx])]
    [:div
     {:class "flex gap-1 items-center"}
     [enrich-existing/start-button
      {:entity primary-entity
       :attachment-id id}]
     (when (seq folder-names)
       [button/square-secondary-open
        {:tooltip (loc/tr :move-to-folder)
         :on-click #(modals/open-modal!
                     folder-selection-modal/modal-key
                     {:entity       attachment
                      :folder-names folder-names
                      :success-callback (fn []
                                          (rf/dispatch [::event/refetch-data ctx]))})}
        [:i
         {:class "fal fa-folder"}]])
     [button/square-secondary-open
      {:tooltip (loc/tr :open)
       :on-click #(rf/dispatch [::event/open-attachment id])}
      [:i {:class "fal fa-external-link"}]]
     [button/square-secondary-open
      {:tooltip (loc/tr :download)
       :on-click #(rf/dispatch [::event/download-attachment id])}
      [:i {:class "fal fa-cloud-download"}]]
     [button/square-secondary-open
      {:tooltip (loc/tr :delete)
       :on-click open-delete-confirmation-modal}
      [:i {:class "fal fa-trash"}]]]))

(defn delete-attachment-confirmation-modal
  [{:keys [set-ref delete-attachment close]}]
  [:dialog
   {:ref set-ref
    :class "rounded border p-0"}
   [:div
    {:class "flex flex-col items-center gap-5 p-4 pt-8 w-96"}
    [:div
     [:i
      {:class "fal fa-exclamation-triangle text-3xl text-danger-regular"}]]
    [:div
     [:p
      {:class "text-center text-lg font-medium mb-2"}
      (loc/tr :remove-file?)]
     [:p
      {:class "text-center text-sm m-0 text-base-light-text-paler"}
      (loc/tr :attachment-delete)]]

    [:div
     {:class "flex gap-1 justify-center"}
     [button/sm-secondary-hollow
      {:on-click close}
      (loc/tr :cancel)]
     [button/sm-danger-solid
      {:on-click (fn []
                   (delete-attachment)
                   (close))}
      (loc/tr :remove)]]]])

(defn attachment-item
  [_]
  (let [delete-modal-ref (atom nil)
        close-delete-confirmation-modal #(when-let [el @delete-modal-ref]
                                           (oops/ocall el "close"))
        open-delete-confirmation-modal #(when-let [el @delete-modal-ref]
                                          (oops/ocall el "showModal"))]
    (fn [{:keys [attachment ctx] :as props}]
      (let [id (:db/id attachment)
            filename (:attachment/filename attachment)
            content-type (:attachment/content-type attachment)]
        [:<>
         [:div
          {:class "group/attachment flex gap-4 items-center"}
          [:button
           {:class "p-0 flex items-center gap-2"
            :on-click #(rf/dispatch [::event/open-attachment id])}
           [attachment-item-left
            {:filename filename
             :content-type content-type}]]
          [:span
           {:class "hidden group-hover/attachment:block"}
           [attachment-actions
            (assoc props :open-delete-confirmation-modal
                   open-delete-confirmation-modal)]]]

         [delete-attachment-confirmation-modal
          {:set-ref #(reset! delete-modal-ref %)
           :close close-delete-confirmation-modal
           :delete-attachment #(rf/dispatch-sync [::event/delete-attachment ctx id])}]]))))

(defn attachments-list
  [{:keys [attachments] :as props}]
  [:div
   {:class "flex flex-col"}
   (->> attachments
        (map (fn [a]
               ^{:key (str (:db/id a))}
               [attachment-item
                (-> props
                    (assoc :attachment a)
                    (dissoc :attachments))])))])

(defn folder-view
  [{:keys [ctx folder-name] :as props}]
  (let [expanded? @(rf/subscribe [::sub/folder-expanded? ctx folder-name])
        attachments @(rf/subscribe [::sub/attachments-in-folder ctx folder-name])
        attachment-count (count attachments)]
    [:div
     [:button
      {:class "p-0 flex items-center cursor-pointer hover:text-base-light-text-paler"
       :on-click #(rf/dispatch [::event/set-folder-expanded ctx {:folder-name folder-name
                                                                 :expanded? (not expanded?)}])}
      [:div
       {:class "w-10 flex justify-start"}
       [:i {:class (uu/cm "fas text-xl"
                          (when (zero? attachment-count)
                            "text-disabled-gloomy")
                          (if expanded?
                            "fa-folder-open"
                            "fa-folder "))}]]
      [:span folder-name]
      [:span
       {:class "ml-1 text-xs m-0 text-base-light-text-paler"}
       (str "(" attachment-count ")")]]
     (when expanded?
       [:div
        {:class "pl-5"}
        [attachments-list (assoc props :attachments attachments)]])]))

(defn folders-view
  [{:keys [ctx] :as props}]
  (let [folder-names @(rf/subscribe [::sub/folder-names ctx])]
    [:div
     {:class "flex flex-col gap-3"}
     (->> folder-names
          (map (fn [folder-name]
                 ^{:key folder-name}
                 [folder-view (assoc props :folder-name folder-name)]))
          (doall))]))

(defn attachments-not-in-folders
  [{:keys [ctx] :as props}]
  (let [attachments-not-in-folders @(rf/subscribe [::sub/attachments-not-in-folders ctx])]
    [attachments-list
     (assoc props :attachments attachments-not-in-folders)]))

(defn content
  [{:keys [ctx] :as props}]
  (let [folder-names @(rf/subscribe [::sub/folder-names ctx])]
    [:div
     {:class "flex flex-col"}
     [folders-view props]
     (when (seq folder-names)
       [:div {:class "h-2"}])
     [attachments-not-in-folders props]
     [uploads-in-progress props]
     [:div
      {:class "mt-3 mb-1"}
      [dropzone props]]]))

(defn component
  [{:keys [card] :as props}]
  (let [title (:card/title card)]
    [card-container/view
     {}
     [:div
      {:class "flex flex-col"}
      [:div
       {:class "flex justify-between items-center"}
       [card-title/view title]
       [:div
        {:class "opacity-0 group-hover/card:opacity-100"}
        [card-shared/expand-card props]]]
      [content props]]]))

(defn modal-component
  [{:keys [card] :as props}]
  (let [title (:card/title card)]
    [full-screen-card/view
     (merge
      props
      {:title title
       :can-edit-content? false})
     [content props]]))

(defn view
  [{:keys [full-screen?] :as props}]
  (if full-screen?
    [modal-component props]
    [component props]))
